import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import {
  activateUser,
  deactivateUser,
  updateUser,
  createUser,
} from "@/services/users";
import { ReadUser, UpdateUser, CreateUser } from "@/types/user";
import { ApiError } from "@/services/api";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { Module } from "@/constants";
import { UserHandlerKey } from "../context/UsersContext";
import { useNavigate } from "react-router-dom";

interface UseUserHandlersProps {
  userId: string;
  openDialog?: (handlerKey: UserHandlerKey, onConfirm: () => void) => void;
  closeDialog?: () => void;
}

export const useUserHandlers = ({
  userId,
  openDialog,
  closeDialog,
}: UseUserHandlersProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const userQueryKey = [Module.Users, userId];

  const activateUserMutation = useMutationWithErrorHandling<
    ReadUser,
    ApiError,
    undefined,
    unknown
  >({
    mutationFn: () => activateUser(userId),
    errorTitle: t("users.errors.activate"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKey });
      queryClient.invalidateQueries({ queryKey: [Module.Users] });
      toast({
        title: t("users.success.activated"),
      });
      closeDialog?.();
    },
  });

  const deactivateUserMutation = useMutationWithErrorHandling<
    ReadUser,
    ApiError,
    undefined,
    unknown
  >({
    mutationFn: () => deactivateUser(userId),
    errorTitle: t("users.errors.deactivate"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKey });
      queryClient.invalidateQueries({ queryKey: [Module.Users] });
      toast({
        title: t("users.success.deactivated"),
      });
      closeDialog?.();
    },
  });

  const updateUserMutation = useMutationWithErrorHandling<
    ReadUser,
    ApiError,
    UpdateUser,
    unknown
  >({
    mutationFn: (updateData) => updateUser(userId, updateData),
    errorTitle: t("users.errors.update"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKey });
      toast({
        title: t("users.success.updated"),
      });
    },
  });

  const createUserMutation = useMutationWithErrorHandling<
    ReadUser,
    ApiError,
    CreateUser,
    unknown
  >({
    mutationFn: createUser,
    errorTitle: t("users.errors.create"),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: [Module.Users] });
      toast({
        title: t("users.success.created"),
      });
      return response;
    },
  });

  const handleActivate = useCallback(() => {
    if (openDialog) {
      openDialog("activate", () => activateUserMutation.mutate(undefined));
    } else {
      activateUserMutation.mutate(undefined);
    }
  }, [activateUserMutation, openDialog]);

  const handleDeactivate = useCallback(() => {
    if (openDialog) {
      openDialog("deactivate", () => deactivateUserMutation.mutate(undefined));
    } else {
      deactivateUserMutation.mutate(undefined);
    }
  }, [deactivateUserMutation, openDialog]);

  const handleUpdate = useCallback(
    async (updateData: UpdateUser) => {
      return updateUserMutation.mutateAsync(updateData);
    },
    [updateUserMutation],
  );

  const handleCreate = useCallback(
    async (createData: CreateUser) => {
      const response = await createUserMutation.mutateAsync(createData);
      if (response) {
        navigate(`/${Module.Users}/${response.id}`);
      }
      return response;
    },
    [createUserMutation],
  );

  const clearError = useCallback(() => {
    activateUserMutation.reset();
    deactivateUserMutation.reset();
    updateUserMutation.reset();
    createUserMutation.reset();
  }, [
    activateUserMutation,
    deactivateUserMutation,
    updateUserMutation,
    createUserMutation,
  ]);

  const mutations = {
    activateUserMutation,
    deactivateUserMutation,
    updateUserMutation,
    createUserMutation,
  };

  return {
    handleActivate,
    handleDeactivate,
    handleUpdate,
    handleCreate,
    clearError,
    mutations,
  };
};
